<template>
  <v-container
    fluid
    class="economic-calendar-page responsive-height main-container"
    :class="{ 'calendar-dark': $vuetify.theme.dark }"
  >
    <v-card elevation="0" class="calendar-shell">
      <div class="calendar-header">
        <div>
          <p class="section-label mb-1">Market schedule</p>
          <h2 class="mb-1">Economic Calendar</h2>
          <p class="calendar-subtitle mb-0">
            Track macro events, forecasts, and actual releases by country and impact.
          </p>
        </div>

        <v-btn
          outlined
          elevation="0"
          class="refresh-btn text-none"
          :loading="loading"
          @click="fetchEconomicData"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>

      <v-row dense class="summary-row">
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card summary-card--events">
            <span class="summary-icon"><v-icon>mdi-calendar-star</v-icon></span>
            <div class="summary-copy">
              <span>Total Events</span>
              <strong>{{ filteredEvents.length }}</strong>
            </div>
            <svg class="summary-sparkline" viewBox="0 0 130 48" preserveAspectRatio="none" aria-hidden="true">
              <polyline points="0,42 14,39 28,32 42,35 56,21 70,29 84,36 98,21 112,27 130,5" />
            </svg>
          </div>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card summary-card--high">
            <span class="summary-icon"><v-icon>mdi-chart-line-variant</v-icon></span>
            <div class="summary-copy">
              <span>High Impact</span>
              <strong>{{ highImpactCount }}</strong>
            </div>
            <svg class="summary-sparkline" viewBox="0 0 130 48" preserveAspectRatio="none" aria-hidden="true">
              <polyline points="0,42 14,40 28,33 42,38 56,28 70,31 84,15 98,28 112,21 130,4" />
            </svg>
          </div>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card summary-card--countries">
            <span class="summary-icon"><v-icon>mdi-chart-bar</v-icon></span>
            <div class="summary-copy">
              <span>Countries</span>
              <strong>{{ selectedCountries.length }}</strong>
            </div>
            <svg class="summary-sparkline" viewBox="0 0 130 48" preserveAspectRatio="none" aria-hidden="true">
              <polyline points="0,43 14,40 28,34 42,25 56,42 70,23 84,30 98,19 112,27 130,4" />
            </svg>
          </div>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <div class="summary-card summary-card--updated">
            <span class="summary-icon"><v-icon>mdi-clock-outline</v-icon></span>
            <div class="summary-copy">
              <span>Last Update</span>
              <strong>{{ lastLoadedText }}</strong>
              <small>{{ lastLoadedDateText }}</small>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-card elevation="0" class="filter-panel">
        <div class="filter-header">
          <div>
            <p class="filter-eyebrow mb-1">Calendar controls</p>
            <h3 class="mb-0">Filter market events</h3>
          </div>

          <div class="filter-actions">
            <v-btn text small class="reset-filter-btn" @click="resetFilters">
              <v-icon left small>mdi-restore</v-icon>
              Reset
            </v-btn>
            <v-btn
              small
              elevation="0"
              class="apply-filter-btn text-none"
              :loading="loading"
              @click="fetchEconomicData"
            >
              <v-icon left small>mdi-filter-check-outline</v-icon>
              Apply Filters
            </v-btn>
          </div>
        </div>

        <div class="quick-range-row">
          <span>Quick range:</span>
          <button
            v-for="preset in datePresets"
            :key="preset.value"
            type="button"
            class="quick-range-chip"
            :class="{ 'quick-range-chip--active': activeDatePreset === preset.value }"
            @click="applyDatePreset(preset.value, true)"
          >
            {{ preset.text }}
          </button>
        </div>

        <v-row dense class="filter-grid">
          <v-col cols="12" sm="6" lg="3">
            <label class="control-label">Start Date</label>
            <v-text-field
              v-model="startDate"
              type="date"
              prepend-inner-icon="mdi-calendar-start"
              hide-details
              outlined
              dense
              @input="activeDatePreset = 'custom'"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <label class="control-label">End Date</label>
            <v-text-field
              v-model="endDate"
              type="date"
              prepend-inner-icon="mdi-calendar-end"
              hide-details
              outlined
              dense
              @input="activeDatePreset = 'custom'"
            ></v-text-field>
          </v-col>

          <v-col cols="12" lg="3">
            <label class="control-label">Countries</label>
            <v-select
              v-model="selectedCountries"
              :items="countryOptions"
              item-text="text"
              item-value="value"
              multiple
              chips
              small-chips
              deletable-chips
              hide-details
              outlined
              dense
              class="country-select"
              placeholder="Select countries"
            >
              <template v-slot:selection="{ item }">
                <v-chip small label close @click:close="removeCountry(item.value)">
                  <span class="country-flag">{{ countryFlag(item.value) }}</span>
                  {{ item.text }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <span class="country-menu-item">
                  <span class="country-flag">{{ countryFlag(item.value) }}</span>
                  {{ item.text }}
                </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" lg="3">
            <label class="control-label">Impact</label>
            <v-select
              v-model="selectedImpacts"
              :items="impactOptions"
              item-text="text"
              item-value="value"
              multiple
              chips
              small-chips
              deletable-chips
              hide-details
              outlined
              dense
              class="impact-select"
              placeholder="Select impact"
            ></v-select>
          </v-col>
        </v-row>
      </v-card>

      <v-card elevation="0" class="search-panel">
        <div class="search-panel-header">
          <div>
            <p class="filter-eyebrow mb-1">Event lookup</p>
            <h3 class="mb-0">Search calendar results</h3>
          </div>
          <span>{{ filteredEvents.length }} results found</span>
        </div>

        <v-text-field
          v-model="search"
          placeholder="Search by event, category, country, currency, or symbol..."
          prepend-inner-icon="mdi-magnify"
          hide-details
          outlined
          dense
          clearable
        ></v-text-field>
      </v-card>

      <v-alert v-if="errorMessage" type="error" dense text class="mt-4">
        {{ errorMessage }}
      </v-alert>

      <v-card elevation="0" class="calendar-table-card">
        <v-data-table
          :headers="headers"
          :items="filteredEvents"
          :loading="loading"
          :items-per-page="25"
          class="calendar-table"
          mobile-breakpoint="768"
        >
          <template v-slot:[`item.date`]="{ item }">
            <div class="date-cell">
              <strong>{{ formatTime(item.date) }}</strong>
              <span>{{ formatDay(item.date) }}</span>
            </div>
          </template>

          <template v-slot:[`item.country`]="{ item }">
            <div class="country-cell">
              <span class="country-flag country-flag--table">{{ countryFlag(item.country?.abbr) }}</span>
              <strong>{{ item.country?.abbr || "-" }}</strong>
            </div>
          </template>

          <template v-slot:[`item.event`]="{ item }">
            <div class="event-cell">
              <strong>{{ item.event || "-" }}</strong>
              <span>{{ item.category || "Calendar" }}</span>
            </div>
          </template>

          <template v-slot:[`item.importance`]="{ item }">
            <v-chip
              small
              label
              :color="impactColor(item.importance)"
              :text-color="impactTextColor(item.importance)"
            >
              {{ impactLabel(item.importance) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actual`]="{ item }">
            <strong :class="valueClass(item.actual, item.forecast, item.better_than_forecast)">
              {{ displayValue(item.actual) }}
            </strong>
          </template>

          <template v-slot:[`item.forecast`]="{ item }">
            {{ displayValue(item.forecast || item.teforecast) }}
          </template>

          <template v-slot:[`item.previous`]="{ item }">
            {{ displayValue(item.previous) }}
          </template>

          <template v-slot:[`item.ticker_name`]="{ item }">
            <v-chip small outlined color="#d99a00">
              {{ item.ticker_name || item.ticker || "-" }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="46">mdi-calendar-alert</v-icon>
              <p>No economic events found for the selected filters.</p>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";

const API_URL = "https://apieconomic.finlogix.com/v1/getEconomicData";

export default {
  name: "EconomicCalendar",

  data() {
    return {
      loading: false,
      errorMessage: "",
      events: [],
      search: "",
      startDate: "",
      endDate: "",
      selectedCountries: ["US", "CH", "NZ", "CA", "CN", "FR", "AU", "JP", "EP", "GB", "DE", "IT"],
      selectedImpacts: [1, 2, 3],
      activeDatePreset: "next3",
      lastLoadedAt: null,
      headers: [
        { text: "Time", value: "date", width: 130 },
        { text: "Country", value: "country", width: 120 },
        { text: "Event", value: "event" },
        { text: "Impact", value: "importance", width: 110 },
        { text: "Actual", value: "actual", width: 110 },
        { text: "Forecast", value: "forecast", width: 110 },
        { text: "Previous", value: "previous", width: 110 },
        { text: "Symbol", value: "ticker_name", width: 120 },
      ],
      countryOptions: [
        { text: "United States", value: "US" },
        { text: "Switzerland", value: "CH" },
        { text: "New Zealand", value: "NZ" },
        { text: "Canada", value: "CA" },
        { text: "China", value: "CN" },
        { text: "France", value: "FR" },
        { text: "Australia", value: "AU" },
        { text: "Japan", value: "JP" },
        { text: "Euro Area", value: "EP" },
        { text: "United Kingdom", value: "GB" },
        { text: "Germany", value: "DE" },
        { text: "Italy", value: "IT" },
      ],
      impactOptions: [
        { text: "Low", value: 1 },
        { text: "Medium", value: 2 },
        { text: "High", value: 3 },
      ],
      datePresets: [
        { text: "Today", value: "today" },
        { text: "Tomorrow", value: "tomorrow" },
        { text: "Next 3 days", value: "next3" },
        { text: "This week", value: "week" },
        { text: "Next week", value: "nextWeek" },
      ],
    };
  },

  computed: {
    actionButtonColor() {
      return this.$vuetify.theme.dark ? "#02b58a" : "#132136";
    },

    filteredEvents() {
      const term = this.search.trim().toLowerCase();

      return this.events
        .filter((item) => this.selectedImpacts.includes(Number(item.importance || 1)))
        .filter((item) => {
          if (!term) return true;

          const searchable = [
            item.event,
            item.category,
            item.country?.name,
            item.country?.abbr,
            item.country?.currency,
            item.ticker_name,
            item.ticker,
            item.symbol,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchable.includes(term);
        })
        .sort((a, b) => new Date(this.normalizedDate(a.date)) - new Date(this.normalizedDate(b.date)));
    },

    highImpactCount() {
      return this.filteredEvents.filter((item) => Number(item.importance) >= 3).length;
    },

    lastLoadedText() {
      if (!this.lastLoadedAt) return "-";
      return this.lastLoadedAt.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    lastLoadedDateText() {
      if (!this.lastLoadedAt) return "Waiting for refresh";
      return this.lastLoadedAt.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },

  mounted() {
    this.setDefaultDates();
    this.fetchEconomicData();
  },

  methods: {
    setDefaultDates() {
      this.applyDatePreset("next3", false);
    },

    applyDatePreset(preset, shouldFetch = false) {
      const start = new Date();
      const end = new Date();

      if (preset === "tomorrow") {
        start.setDate(start.getDate() + 1);
        end.setDate(end.getDate() + 1);
      } else if (preset === "next3") {
        end.setDate(start.getDate() + 3);
      } else if (preset === "week") {
        const day = start.getDay();
        const daysUntilSunday = day === 0 ? 0 : 7 - day;
        end.setDate(start.getDate() + daysUntilSunday);
      } else if (preset === "nextWeek") {
        const day = start.getDay();
        const daysUntilMonday = day === 0 ? 1 : 8 - day;
        start.setDate(start.getDate() + daysUntilMonday);
        end.setTime(start.getTime());
        end.setDate(start.getDate() + 6);
      }

      this.startDate = this.toDateInput(start);
      this.endDate = this.toDateInput(end);
      this.activeDatePreset = preset;

      if (shouldFetch) {
        this.fetchEconomicData();
      }
    },

    resetFilters() {
      this.search = "";
      this.selectedCountries = this.countryOptions.map((country) => country.value);
      this.selectedImpacts = this.impactOptions.map((impact) => impact.value);
      this.applyDatePreset("next3", true);
    },

    removeCountry(countryCode) {
      this.selectedCountries = this.selectedCountries.filter(code => code !== countryCode);
    },

    countryFlag(countryCode) {
      const code = String(countryCode || "").toUpperCase();
      if (code === "EP" || code === "EU" || code === "EA") return "🇪🇺";
      if (code === "UK") return "🇬🇧";
      if (!/^[A-Z]{2}$/.test(code)) return "🌐";
      return code
        .split("")
        .map(character => String.fromCodePoint(127397 + character.charCodeAt(0)))
        .join("");
    },

    async fetchEconomicData() {
      if (!this.startDate || !this.endDate || !this.selectedCountries.length) {
        this.errorMessage = "Select a start date, end date, and at least one country.";
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      try {
        const { data } = await axios.get(API_URL, {
          params: {
            start: `${this.startDate}T00:00:00Z`,
            end: `${this.endDate}T23:59:59Z`,
            countries: this.selectedCountries.join(","),
          },
        });

        this.events = Array.isArray(data?.data) ? data.data : [];
        this.lastLoadedAt = new Date();
      } catch (error) {
        console.error(error);
        this.events = [];
        this.errorMessage = "Unable to load the economic calendar. If this continues, the API may need a backend proxy.";
      } finally {
        this.loading = false;
      }
    },

    toDateInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    normalizedDate(date) {
      if (!date) return "";
      return date.includes("T") ? date : date.replace(" ", "T");
    },

    formatTime(date) {
      if (!date) return "-";
      return new Date(this.normalizedDate(date)).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDay(date) {
      if (!date) return "-";
      return new Date(this.normalizedDate(date)).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },

    displayValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },

    impactLabel(importance) {
      const level = Number(importance);
      if (level >= 3) return "High";
      if (level === 2) return "Medium";
      return "Low";
    },

    impactColor(importance) {
      const level = Number(importance);
      if (level >= 3) return "#e55353";
      if (level === 2) return "#ffd166";
      return this.$vuetify.theme.dark ? "#26384f" : "#d8e3ec";
    },

    impactTextColor(importance) {
      const level = Number(importance);
      if (level >= 3) return "white";
      if (level === 2) return "#132136";
      return this.$vuetify.theme.dark ? "#e0eaf2" : "#132136";
    },

    valueClass(actual, forecast, direction) {
      if (actual === null || actual === undefined || forecast === null || forecast === undefined || !direction) return "";

      const actualNumber = parseFloat(String(actual).replace(/[^0-9.-]/g, ""));
      const forecastNumber = parseFloat(String(forecast).replace(/[^0-9.-]/g, ""));

      if (Number.isNaN(actualNumber) || Number.isNaN(forecastNumber)) return "";

      const isBetter =
        direction === "larger"
          ? actualNumber > forecastNumber
          : actualNumber < forecastNumber;

      return isBetter ? "value-positive" : "value-negative";
    },
  },
};
</script>

<style scoped>
.economic-calendar-page {
  --calendar-page-bg: #f7f8fa;
  --calendar-surface: #ffffff;
  --calendar-surface-soft: #f4f5f7;
  --calendar-surface-raised: #ffffff;
  --calendar-text: #15181d;
  --calendar-muted: #626973;
  --calendar-muted-soft: #858b94;
  --calendar-border: #e0e3e7;
  --calendar-border-soft: #e8eaed;
  --calendar-table-hover: rgba(217, 154, 0, 0.055);
  --calendar-input-bg: #ffffff;
  --calendar-primary: #d99a00;
  --calendar-primary-hover: #b47f00;
  --calendar-primary-soft: #fff7df;
  --calendar-success: #d99a00;
  --calendar-shadow: 0 8px 26px rgba(29, 46, 80, 0.045);
  width: 100%;
  max-width: none !important;
  min-height: 100%;
  padding: 18px 20px 30px;
  background: var(--calendar-page-bg);
  color: var(--calendar-text);
}

.calendar-dark {
  --calendar-page-bg: #030b13;
  --calendar-surface: #07111b;
  --calendar-surface-soft: #101923;
  --calendar-surface-raised: #0b1621;
  --calendar-text: #f3f6fd;
  --calendar-muted: #aeb6c0;
  --calendar-muted-soft: #858f99;
  --calendar-border: #293026;
  --calendar-border-soft: #242b24;
  --calendar-table-hover: rgba(227, 166, 0, 0.08);
  --calendar-input-bg: #08131e;
  --calendar-primary: #ffc400;
  --calendar-primary-hover: #ffd34f;
  --calendar-primary-soft: rgba(227, 166, 0, 0.13);
  --calendar-success: #ffc400;
  --calendar-shadow: 0 14px 35px rgba(0, 0, 0, 0.14);
}

.calendar-shell {
  padding: 18px 16px 16px;
  border: 1px solid var(--calendar-border-soft);
  border-radius: 10px !important;
  background:
    radial-gradient(circle at 75% 0%, rgba(217, 154, 0, 0.035), transparent 22%),
    var(--calendar-surface) !important;
  box-shadow: var(--calendar-shadow);
}

.calendar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  padding: 0 2px;
}

.section-label {
  color: var(--calendar-success);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.calendar-header h2 {
  color: var(--calendar-text);
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.35px;
  line-height: 1.25;
}

.calendar-subtitle {
  color: var(--calendar-muted);
  font-size: 12px;
}

.refresh-btn {
  min-width: 104px;
  height: 37px !important;
  border-color: var(--calendar-primary) !important;
  border-radius: 6px !important;
  color: var(--calendar-text) !important;
  background: var(--calendar-surface-raised) !important;
  font-size: 12px !important;
  letter-spacing: 0 !important;
}

.summary-row {
  margin: 0 -5px 10px;
}

.summary-row > .col,
.summary-row > [class*="col-"] {
  padding: 5px;
}

.summary-card {
  position: relative;
  display: flex;
  min-height: 91px;
  align-items: center;
  gap: 13px;
  overflow: hidden;
  border: 1px solid var(--calendar-border);
  border-radius: 7px;
  padding: 14px;
  background: linear-gradient(140deg, var(--calendar-surface-raised), var(--calendar-surface-soft));
  box-shadow: 0 6px 18px rgba(24, 42, 76, 0.025);
}

.summary-icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #ffc826, #a97700);
  box-shadow: 0 7px 18px rgba(191, 137, 0, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.summary-icon .v-icon {
  color: white !important;
  font-size: 25px !important;
}

.summary-copy {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 75px;
  flex-direction: column;
}

.summary-copy > span {
  color: var(--calendar-muted);
  font-size: 10px;
  font-weight: 500;
}

.summary-copy > strong {
  margin-top: 6px;
  color: var(--calendar-text);
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.4px;
  line-height: 1;
}

.summary-copy > small {
  margin-top: 4px;
  color: var(--calendar-muted);
  font-size: 9px;
  white-space: nowrap;
}

.summary-card--high .summary-icon {
  background: linear-gradient(145deg, #32a957, #187338);
  box-shadow: 0 7px 18px rgba(38, 159, 78, 0.18), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.summary-card--countries .summary-icon {
  background: linear-gradient(145deg, #ffc826, #a97700);
  box-shadow: 0 7px 18px rgba(191, 137, 0, 0.18), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.summary-card--updated .summary-icon {
  background: linear-gradient(145deg, #ffc826, #a97700);
  box-shadow: 0 7px 18px rgba(191, 137, 0, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.summary-sparkline {
  position: absolute;
  right: 8px;
  bottom: 15px;
  width: 43%;
  height: 42px;
  overflow: visible;
}

.summary-sparkline polyline {
  fill: none;
  stroke: #d99a00;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.2;
  vector-effect: non-scaling-stroke;
}

.summary-card--events .summary-sparkline {
  filter: drop-shadow(0 8px 7px rgba(191, 137, 0, 0.18));
}

.summary-card--high .summary-sparkline polyline {
  stroke: #31c56b;
}

.summary-card--high .summary-sparkline {
  filter: drop-shadow(0 8px 7px rgba(49, 197, 107, 0.17));
}

.summary-card--countries .summary-sparkline polyline {
  stroke: #d99a00;
}

.summary-card--countries .summary-sparkline {
  filter: drop-shadow(0 8px 7px rgba(191, 137, 0, 0.18));
}

.filter-panel {
  border: 1px solid var(--calendar-border);
  border-radius: 7px !important;
  padding: 14px 15px 12px;
  background: linear-gradient(145deg, var(--calendar-surface), var(--calendar-surface-soft)) !important;
}

.search-panel {
  margin-top: 9px;
  border: 1px solid var(--calendar-border);
  border-bottom: 0;
  border-radius: 7px 7px 0 0 !important;
  padding: 12px 15px 11px;
  background: var(--calendar-surface) !important;
}

.filter-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 9px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--calendar-border);
}

.search-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.search-panel-header h3 {
  color: var(--calendar-text);
  font-size: 13px;
  font-weight: 650;
  line-height: 1.25;
}

.search-panel-header span {
  border-radius: 5px;
  padding: 7px 11px;
  color: var(--calendar-success);
  background: rgba(15, 187, 114, 0.08);
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.filter-eyebrow {
  color: var(--calendar-success);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15px;
  text-transform: uppercase;
}

.filter-header h3 {
  color: var(--calendar-text);
  font-size: 13px;
  font-weight: 650;
  line-height: 1.25;
}

.filter-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.reset-filter-btn {
  height: 34px !important;
  border: 1px solid var(--calendar-border) !important;
  border-radius: 5px !important;
  color: var(--calendar-muted) !important;
  background: var(--calendar-surface-raised) !important;
  font-size: 11px !important;
  letter-spacing: 0 !important;
}

.apply-filter-btn {
  height: 34px !important;
  border-radius: 5px !important;
  color: #17130a !important;
  background: linear-gradient(180deg, #ffc826, #e4a500) !important;
  box-shadow: 0 6px 15px rgba(191, 137, 0, 0.18) !important;
  font-size: 11px !important;
  letter-spacing: 0 !important;
}

.quick-range-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
  margin-bottom: 10px;
}

.quick-range-row span {
  color: var(--calendar-muted);
  font-size: 10px;
  font-weight: 500;
  margin-right: 7px;
}

.quick-range-chip {
  min-width: 58px;
  height: 30px;
  border: 1px solid var(--calendar-border);
  border-radius: 5px;
  padding: 0 13px;
  color: var(--calendar-muted);
  background: var(--calendar-surface-raised);
  cursor: pointer;
  font-family: inherit;
  font-size: 10px;
  font-weight: 500;
  transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;
}

.quick-range-chip:hover,
.quick-range-chip--active {
  border-color: var(--calendar-primary);
  color: #17130a;
  background: linear-gradient(180deg, #ffc826, #e4a500);
  box-shadow: 0 5px 12px rgba(191, 137, 0, 0.18);
}

.filter-grid {
  margin: 0 -6px;
  padding-top: 8px;
  border-top: 1px solid var(--calendar-border);
  row-gap: 7px;
}

.filter-grid > .col,
.filter-grid > [class*="col-"] {
  padding: 5px 6px;
}

.control-label {
  display: block;
  margin-bottom: 6px;
  color: var(--calendar-muted);
  font-size: 10px;
  font-weight: 500;
}

.calendar-table-card {
  overflow: hidden;
  border: 1px solid var(--calendar-border);
  border-radius: 0 0 7px 7px !important;
  background: var(--calendar-surface) !important;
}

.calendar-table {
  color: var(--calendar-text);
  background: var(--calendar-surface);
}

.date-cell,
.event-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.country-cell {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.date-cell strong,
.country-cell strong,
.event-cell strong {
  color: var(--calendar-text);
  font-size: 11px;
  font-weight: 600;
}

.date-cell > span,
.event-cell span {
  color: var(--calendar-muted);
  font-size: 9px;
}

.event-cell strong {
  white-space: normal;
}

.country-flag {
  display: inline-block;
  flex: 0 0 auto;
  margin-right: 4px;
  font-size: 13px;
  line-height: 1;
}

.country-flag--table {
  margin: 0;
  font-size: 15px;
}

.country-menu-item {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.value-positive {
  color: #0fbb72;
}

.value-negative {
  color: #ff4141;
}

.empty-state {
  padding: 36px 12px;
  text-align: center;
  color: var(--calendar-muted);
}

.empty-state .v-icon { color: var(--calendar-primary) !important; }

.calendar-table-card ::v-deep .v-data-table {
  background: var(--calendar-surface);
  color: var(--calendar-text);
}

.calendar-table-card ::v-deep .v-data-table-header th {
  background: var(--calendar-surface-soft);
  color: var(--calendar-muted) !important;
  border-bottom: 1px solid var(--calendar-border) !important;
  font-size: 9px !important;
  font-weight: 550 !important;
  height: 37px !important;
}

.calendar-table-card ::v-deep .v-data-table tbody tr {
  background: var(--calendar-surface);
}

.calendar-table-card ::v-deep .v-data-table tbody tr:hover {
  background: var(--calendar-table-hover) !important;
}

.calendar-table-card ::v-deep .v-data-table td {
  height: 48px;
  color: var(--calendar-text);
  border-bottom: 1px solid var(--calendar-border) !important;
  font-size: 10px;
}

.calendar-table-card ::v-deep .v-data-footer {
  background: var(--calendar-surface);
  color: var(--calendar-muted);
  border-top: 1px solid var(--calendar-border);
}

.calendar-table-card ::v-deep .v-data-footer__select,
.calendar-table-card ::v-deep .v-data-footer__pagination {
  color: var(--calendar-muted);
}

.calendar-table-card ::v-deep .v-chip {
  height: 23px;
  border-radius: 4px !important;
  font-size: 9px;
}

.calendar-table-card ::v-deep .v-chip.v-chip--outlined {
  border-radius: 12px !important;
  background: rgba(15, 187, 114, 0.04) !important;
}

.filter-panel ::v-deep .v-input__slot,
.search-panel ::v-deep .v-input__slot {
  min-height: 39px !important;
  margin-bottom: 0 !important;
  border-radius: 5px !important;
  background: var(--calendar-input-bg) !important;
  color: var(--calendar-text);
  box-shadow: none !important;
}

.filter-panel ::v-deep .v-label,
.filter-panel ::v-deep input,
.filter-panel ::v-deep .v-select__selection,
.filter-panel ::v-deep .v-icon,
.search-panel ::v-deep .v-label,
.search-panel ::v-deep input,
.search-panel ::v-deep .v-icon {
  color: var(--calendar-primary) !important;
  font-size: 18px;
}

.filter-panel ::v-deep input,
.filter-panel ::v-deep .v-select__selection,
.search-panel ::v-deep input {
  color: var(--calendar-primary) !important;
  font-size: 10px;
}

.filter-panel ::v-deep fieldset,
.search-panel ::v-deep fieldset {
  border-color: var(--calendar-border) !important;
}

.filter-panel ::v-deep .v-chip {
  max-width: 150px;
  height: 24px;
  margin: 2px 3px 2px 0;
  border: 1px solid var(--calendar-border);
  border-radius: 4px !important;
  color: var(--calendar-text) !important;
  background: var(--calendar-surface-soft) !important;
  font-size: 8px;
}

.filter-panel ::v-deep .v-chip__content {
  min-width: 0;
}

.filter-panel ::v-deep .v-select .v-chip__content span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-panel ::v-deep .v-chip__close {
  width: 18px !important;
  height: 18px !important;
  min-width: 18px !important;
  border-radius: 50% !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  flex: 0 0 18px;
  background: rgba(96, 112, 131, 0.18);
  color: var(--calendar-muted) !important;
  opacity: 1;
}

.filter-panel ::v-deep .v-chip__close::before {
  border-radius: 50% !important;
}

.filter-panel ::v-deep .v-chip__close:hover {
  background: rgba(229, 83, 83, 0.16);
  color: #e55353 !important;
}

.country-select::v-deep .v-input__slot {
  min-height: 74px !important;
  align-items: flex-start;
  padding-top: 3px !important;
}

.country-select::v-deep .v-select__slot {
  align-items: flex-start;
}

.country-select::v-deep .v-select__selections {
  padding: 2px 0 !important;
}

.country-select::v-deep .v-input__append-inner {
  align-self: center;
  margin-top: 8px !important;
}

.impact-select::v-deep .v-input__slot {
  align-items: flex-start;
}

.impact-select::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}

.search-panel::v-deep .v-text-field__slot input::placeholder {
  color: var(--calendar-muted) !important;
  opacity: 0.9;
}

.calendar-dark .calendar-shell {
  background:
    radial-gradient(circle at 76% 0%, rgba(227, 166, 0, 0.055), transparent 25%),
    linear-gradient(145deg, #07111b, #08131e) !important;
}

.calendar-dark .summary-card {
  box-shadow: inset 0 0 28px rgba(57, 104, 169, 0.025);
}

.calendar-dark .filter-panel {
  background: linear-gradient(145deg, #07111b, #0b1621) !important;
}

@media (max-width: 1260px) {
  .summary-sparkline {
    width: 36%;
    opacity: 0.75;
  }

  .summary-card {
    gap: 10px;
    padding: 12px;
  }
}

@media (max-width: 959px) {
  .economic-calendar-page {
    padding: 14px 12px 24px;
  }

  .filter-grid {
    border-top: 0;
  }

  .summary-sparkline {
    width: 45%;
  }
}

@media (max-width: 768px) {
  .calendar-shell {
    padding: 12px;
  }

  .calendar-header {
    flex-direction: column;
  }

  .refresh-btn {
    width: 100%;
  }

  .filter-header {
    flex-direction: column;
  }

  .filter-actions,
  .filter-actions {
    width: 100%;
  }

  .filter-actions .v-btn {
    flex: 1 1 0;
  }

  .quick-range-row {
    align-items: flex-start;
  }

  .quick-range-row span {
    width: 100%;
  }

  .search-panel-header {
    flex-direction: column;
  }

  .search-panel-header span {
    align-self: stretch;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .economic-calendar-page {
    padding: 10px 7px 18px;
  }

  .calendar-header h2 {
    font-size: 20px;
  }

  .quick-range-row {
    align-items: stretch;
  }

  .quick-range-chip {
    flex: 1 1 calc(50% - 9px);
  }

  .summary-sparkline {
    width: 48%;
  }
}
</style>
